import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { firmFromQS } from "@/lib/nav";
import api from "@/lib/api";
import ExportButton from "@/components/ExportButton";
import ChurnDialog from "@/components/ChurnDialog";
import { FIRM_BASE_COLUMNS, USER_COLUMNS } from "@/lib/exportColumns";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { TierBadge, UsersCell, PocChipPair } from "@/components/Badges";
import { FirmSubmeta } from "@/components/FirmSubmeta";
import { useConfirm } from "@/components/ConfirmDialog";
import ContactsPanel from "@/components/ContactsPanel";
import {
  Plus,
  UploadSimple,
  PencilSimple,
  Trash,
  CheckCircle,
  XCircle,
  X,
  MagnifyingGlass,
  Users,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import DataSync from "@/pages/DataSync";
import UsersTab from "@/pages/UsersTab";
import ImportHistoryTab from "@/pages/ImportHistoryTab";
import DbStatsTab from "@/pages/DbStatsTab";

const EMPTY_FORM = {
  subdomain: "",
  firm_name: "",
  tier: "Premium",
  city: "",
  poc: "Shankar",
  sales_poc: "",
  activation_date: new Date().toISOString().slice(0, 10),
  subscription_end_date: "",
  status: "Live",
  num_users: "",
  cloud_plan: "",  // Enterprise-only; required when tier === "Enterprise"
};

// Date math helper — clones a YYYY-MM-DD activation date forward by one year,
// handling Feb 29 → Feb 28 the same way the backend does.
function addOneYear(iso) {
  if (!iso || iso.length < 10) return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const next = new Date(Date.UTC(y + 1, m - 1, d));
  if (next.getUTCMonth() !== m - 1) {
    return `${y + 1}-${String(m).padStart(2, "0")}-28`;
  }
  return next.toISOString().slice(0, 10);
}

export default function Admin() {
  const { user: me } = useAuth();
  const confirm = useConfirm();
  const isSuperAdmin = me?.role === "super_admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "firms";
  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "firms") next.delete("tab");
    else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  const [firms, setFirms] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [grandTotal, setGrandTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");
  const [pocOptions, setPocOptions] = useState([]);
  const [tierOptions, setTierOptions] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [churnFirm, setChurnFirm] = useState(null);
  const [showPocMgr, setShowPocMgr] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        search: search || undefined,
        tier: tierFilter !== "all" ? tierFilter : undefined,
        status: statusFilter,
        page,
        page_size: PAGE_SIZE,
      };
      const r = await api.get("/firms", { params });
      setFirms(r.data.firms);
      setTotal(r.data.total);
      setPages(Math.max(1, Math.ceil((r.data.total || 0) / PAGE_SIZE)));
      if (!search && tierFilter === "all" && statusFilter === "all") {
        setGrandTotal(r.data.total);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPocs = () => {
    api.get("/meta/options").then((r) => {
      setPocOptions(r.data.pocs);
      setTierOptions(r.data.tiers);
    });
  };

  useEffect(() => {
    loadPocs();
    // initial firm count for tab badge regardless of which tab is open
    api.get("/firms", { params: { page: 1, page_size: 1 } }).then((r) => {
      setTotal(r.data.total);
    });
  }, []);

  useEffect(() => {
    if (tab !== "firms") return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, tierFilter, tab, page]);

  // Reset to first page whenever a filter/search changes so users don't get
  // stuck on a now-empty page.
  useEffect(() => { setPage(1); }, [search, statusFilter, tierFilter]);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setShowAdd(true);
  };

  const openEdit = (firm) => {
    setForm({
      subdomain: firm.subdomain,
      firm_name: firm.firm_name,
      tier: firm.tier,
      city: firm.city,
      poc: firm.poc,
      sales_poc: firm.sales_poc || "",
      activation_date: firm.activation_date,
      subscription_end_date: firm.subscription_end_date || addOneYear(firm.activation_date),
      status: firm.status,
      num_users: firm.num_users === "-" ? "" : (firm.num_users || ""),
      cloud_plan: firm.cloud_plan || "",
    });
    setEditing(firm);
    setShowAdd(true);
  };

  const submit = async () => {
    try {
      // Strip empty-string fields so the PUT payload never accidentally sends
      // "" for a date / subdomain etc. (the backend validator rejects "").
      // Exception: `num_users === ""` is intentionally allowed through so the
      // backend normalises it to "-" (clearing back to "no data").
      // `sales_poc === ""` is also allowed: backend treats it as "clear it".
      const payload = Object.fromEntries(
        Object.entries(form).filter(([k, v]) => {
          if (k === "num_users") return v !== null && v !== undefined;
          if (k === "sales_poc") return v !== null && v !== undefined;
          return v !== "" && v !== null && v !== undefined;
        })
      );
      if (editing) {
        await api.put(`/firms/${editing.subdomain}`, payload);
        toast.success("Firm updated");
        setShowAdd(false);
        setEditing(null);
        load();
      } else {
        const res = await api.post("/firms", payload);
        toast.success("Firm created — add contacts below");
        // Switch dialog into edit mode for the newly created firm so the
        // ContactsPanel renders and the admin can immediately add contacts.
        // Critically: sync `form` to the canonical values returned by the
        // backend (subscription_end_date is auto-computed server-side, and
        // would otherwise stay empty in the form, causing the next PUT to
        // send "" and fail validation).
        setEditing(res.data);
        setForm({
          subdomain: res.data.subdomain || "",
          firm_name: res.data.firm_name || "",
          tier: res.data.tier || "Premium",
          city: res.data.city || "",
          poc: res.data.poc || "",
          sales_poc: res.data.sales_poc || "",
          activation_date: res.data.activation_date || "",
          subscription_end_date: res.data.subscription_end_date || "",
          status: res.data.status || "Live",
          num_users: res.data.num_users === "-" ? "" : (res.data.num_users || ""),
          cloud_plan: res.data.cloud_plan || "",
        });
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const toggleStatus = async (firm) => {
    if (firm.status === "Live" || firm.status === "Renewal Pending") {
      // Both Live and Renewal Pending firms are still customers — offer Mark Churned.
      setChurnFirm(firm);
    } else {
      // Reactivate flow handled via dedicated button + dialog confirm
      reactivate(firm);
    }
  };

  const remove = async (firm) => {
    if (!await confirm({
      title: "Delete firm?",
      body: `${firm.firm_name} will be permanently removed. This cannot be undone.`,
      confirmLabel: "Delete firm",
      destructive: true,
    })) return;
    try {
      await api.delete(`/firms/${firm.subdomain}`);
      toast.success("Firm deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const reactivate = async (firm) => {
    if (!await confirm({
      title: "Reactivate firm?",
      body: `${firm.firm_name}'s status will flip back to Live.`,
      confirmLabel: "Reactivate",
    })) return;
    try {
      await api.post(`/firms/${firm.subdomain}/reactivate`, { notes: "" });
      toast.success("Reactivated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reactivation failed");
    }
  };

  const handleNormalizeCities = async () => {
    if (!await confirm({
      title: "Normalize City Names?",
      body: "This will correct all city names to proper capitalization and standardize variations (e.g., 'BANGALORE' → 'Bengaluru', 'MUMBAI' → 'Mumbai'). Safe to run multiple times.",
      confirmLabel: "Normalize Cities",
    })) return;
    
    try {
      toast.loading("Normalizing city names...", { id: "normalize-cities" });
      const res = await api.post("/admin/firms/normalize-cities");
      toast.success(
        `City normalization complete! Updated ${res.data.updated_count} firms, ${res.data.unchanged_count} already correct.`,
        { id: "normalize-cities", duration: 5000 }
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "City normalization failed", { id: "normalize-cities" });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900">
          Admin
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage firms, run data sync from Mixpanel + Grafana.
        </p>
      </div>

      <div className="flex items-center gap-1 border-b border-gray-200" data-testid="admin-tabs">
        <TabBtn active={tab === "firms"} onClick={() => setTab("firms")} testid="tab-firms">
          Firms
          <span className="ml-2 text-[10px] bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5">
            {grandTotal}
          </span>
        </TabBtn>
        <TabBtn active={tab === "users"} onClick={() => setTab("users")} testid="tab-users">
          Users
        </TabBtn>
        {isSuperAdmin ? (
          <>
            <TabBtn active={tab === "sync"} onClick={() => setTab("sync")} testid="tab-sync">
              Data Sync
            </TabBtn>
            <TabBtn active={tab === "import"} onClick={() => setTab("import")} testid="tab-import">
              Import History
            </TabBtn>
            <TabBtn active={tab === "dbstats"} onClick={() => setTab("dbstats")} testid="tab-dbstats">
              DB Stats
            </TabBtn>
          </>
        ) : null}
      </div>

      {isSuperAdmin && tab === "sync" ? (
        <DataSync />
      ) : tab === "users" ? (
        <UsersTab />
      ) : isSuperAdmin && tab === "import" ? (
        <ImportHistoryTab />
      ) : isSuperAdmin && tab === "dbstats" ? (
        <DbStatsTab />
      ) : (
        <FirmsTab
          firms={firms}
          loading={loading}
          search={search}
          setSearch={setSearch}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          tierFilter={tierFilter}
          setTierFilter={setTierFilter}
          tierOptions={tierOptions}
          openAdd={openAdd}
          openEdit={openEdit}
          toggleStatus={toggleStatus}
          remove={remove}
          isSuperAdmin={isSuperAdmin}
          openPocMgr={() => setShowPocMgr(true)}
          handleNormalizeCities={handleNormalizeCities}
          page={page}
          setPage={setPage}
          pages={pages}
          total={total}
        />
      )}

      <Dialog open={showAdd} onOpenChange={(v) => { setShowAdd(v); if (!v) setEditing(null); }}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="firm-form-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit firm" : "Add new firm"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Firm name" required={!editing}>
              <Input
                value={form.firm_name}
                onChange={(e) => setForm({ ...form, firm_name: e.target.value })}
                data-testid="form-firm-name"
              />
            </Field>
            <Field
              label="Subdomain"
              required={!editing}
              hint={
                editing
                  ? "Locked after creation — used as the firm's unique identifier across Mixpanel/Grafana joins."
                  : 'The unique AssureAI URL slug — the part before ".assureai.in" (e.g. mss → mss.assureai.in). Lowercase, no spaces. For custom domains (e.g. assureai-tax.gurujana.com) enter the full URL including dots; we will use it verbatim as firm_url. This is critical — it links events from Mixpanel + Grafana to this firm and cannot be changed later.'
              }
            >
              <Input
                value={form.subdomain}
                onChange={(e) =>
                  setForm({
                    ...form,
                    subdomain: e.target.value.toLowerCase().replace(/\s/g, ""),
                  })
                }
                disabled={!!editing}
                placeholder="e.g. mss"
                data-testid="form-subdomain"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tier" required={!editing}>
                <Select
                  value={form.tier}
                  onValueChange={(v) =>
                    setForm({
                      ...form,
                      tier: v,
                      // Clear cloud_plan when leaving Enterprise; force re-pick if switching to Enterprise.
                      cloud_plan: v === "Enterprise" ? form.cloud_plan : "",
                    })
                  }
                >
                  <SelectTrigger data-testid="form-tier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tierOptions.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Support POC" required={!editing}>
                <Select value={form.poc} onValueChange={(v) => setForm({ ...form, poc: v })}>
                  <SelectTrigger data-testid="form-poc">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pocOptions.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Sales POC" hint="Optional">
                <Select
                  value={form.sales_poc || "__none__"}
                  onValueChange={(v) => setForm({ ...form, sales_poc: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger data-testid="form-sales-poc">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {pocOptions.map((p) => (
                      <SelectItem key={`sales-${p}`} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {form.tier === "Enterprise" && (
                <Field label="Cloud plan" required>
                  <Select
                    value={form.cloud_plan || ""}
                    onValueChange={(v) => setForm({ ...form, cloud_plan: v })}
                  >
                    <SelectTrigger data-testid="form-cloud-plan">
                      <SelectValue placeholder="Pick a cloud" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AssureAI Cloud">AssureAI Cloud</SelectItem>
                      <SelectItem value="Own Cloud">Own Cloud</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] text-gray-500 mt-1">
                    Enterprise-only. No default — please pick one.
                  </div>
                </Field>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City" required={!editing}>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="form-city"
                />
              </Field>
              <Field label="Activation date" required={!editing}>
                <Input
                  type="date"
                  value={form.activation_date}
                  onChange={(e) => {
                    const newAct = e.target.value;
                    // Auto-shift the subscription end date if the user hadn't customised it,
                    // OR if it was still aligned with activation+1y (so a quick activation edit
                    // doesn't leave a stale subscription date behind).
                    const oldDefault = addOneYear(form.activation_date);
                    const userTouched =
                      form.subscription_end_date && form.subscription_end_date !== oldDefault;
                    setForm({
                      ...form,
                      activation_date: newAct,
                      subscription_end_date: userTouched ? form.subscription_end_date : addOneYear(newAct),
                    });
                  }}
                  data-testid="form-activation-date"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Subscription end date" required={!editing}>
                <Input
                  type="date"
                  value={form.subscription_end_date || addOneYear(form.activation_date)}
                  min={form.activation_date}
                  onChange={(e) => setForm({ ...form, subscription_end_date: e.target.value })}
                  data-testid="form-subscription-end-date"
                />
                <div className="text-[10px] text-gray-500 mt-1">
                  Defaults to activation + 1 year. Team can edit anytime.
                </div>
              </Field>
              <Field label="Status" required={!editing}>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="form-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Live">Live</SelectItem>
                    <SelectItem value="Churned">Churned</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Number of users">
                <Input
                  type="text"
                  placeholder="e.g. 12 or unlimited"
                  value={form.num_users}
                  onChange={(e) => setForm({ ...form, num_users: e.target.value })}
                  data-testid="form-num-users"
                />
                <div className="text-[10px] text-gray-500 mt-1">
                  Enter a number or type <span className="font-mono">unlimited</span>. Leave blank if unknown — stored as &mdash;.
                </div>
              </Field>
            </div>
          </div>

          {editing && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              <ContactsPanel firmSubdomain={editing.subdomain} compact />
            </div>
          )}

          {editing && isSuperAdmin && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              <AliasesEditor
                firm={editing}
                onChange={(next) => setEditing({ ...editing, aliases: next })}
              />
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowAdd(false); setEditing(null); }}
              data-testid="form-cancel"
            >
              {editing ? "Done" : "Cancel"}
            </Button>
            <Button
              onClick={submit}
              disabled={
                (form.tier === "Enterprise" && !form.cloud_plan) ||
                (!editing && (
                  !form.firm_name.trim() ||
                  !form.subdomain.trim() ||
                  !form.tier ||
                  !form.poc ||
                  !form.city.trim() ||
                  !form.activation_date ||
                  !(form.subscription_end_date || addOneYear(form.activation_date)) ||
                  !form.status
                ))
              }
              className="bg-gray-900 hover:bg-gray-800 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="form-submit"
            >
              {editing ? "Save changes" : "Create firm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChurnDialog
        firm={churnFirm}
        onClose={() => setChurnFirm(null)}
        onConfirmed={() => {
          setChurnFirm(null);
          load();
        }}
      />
      <PocManagerDialog
        open={showPocMgr}
        onClose={() => setShowPocMgr(false)}
        onChanged={() => { loadPocs(); load(); }}
      />
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`pb-2.5 px-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
        active
          ? "border-gray-900 text-gray-900"
          : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function FirmsTab({
  firms,
  loading,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  tierFilter,
  setTierFilter,
  tierOptions,
  openAdd,
  openEdit,
  toggleStatus,
  remove,
  isSuperAdmin,
  openPocMgr,
  handleNormalizeCities,
  page,
  setPage,
  pages,
  total,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showSeedDlg, setShowSeedDlg] = useState(false);
  const [showCloudDlg, setShowCloudDlg] = useState(false);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end gap-2 flex-wrap">
        {isSuperAdmin && (
          <Button
            variant="outline"
            onClick={openPocMgr}
            data-testid="manage-pocs-btn"
          >
            <Users size={16} className="mr-1.5" />
            Manage POCs
          </Button>
        )}
        {isSuperAdmin && (
          <Button
            variant="outline"
            onClick={handleNormalizeCities}
            data-testid="normalize-cities-btn"
          >
            <CheckCircle size={16} className="mr-1.5" />
            Normalize Cities
          </Button>
        )}
        {isSuperAdmin && (
          <Button
            variant="outline"
            onClick={() => setShowSeedDlg(true)}
            data-testid="seed-num-users-btn"
          >
            <UploadSimple size={16} className="mr-1.5" />
            Import user counts
          </Button>
        )}
        {isSuperAdmin && (
          <Button
            variant="outline"
            onClick={() => setShowCloudDlg(true)}
            data-testid="import-cloud-plan-btn"
          >
            <UploadSimple size={16} className="mr-1.5" />
            Import cloud plan
          </Button>
        )}
        <ExportButton
          filename="admin-firms"
          sheetName="Firms"
          columns={FIRM_BASE_COLUMNS}
          fetchRows={async () => {
            const params = {
              search: search || undefined,
              status: statusFilter !== "all" ? statusFilter : undefined,
              page: 1,
              page_size: 10000,
            };
            const r = await api.get("/firms", { params });
            return r.data.firms || [];
          }}
          testId="export-admin-firms"
        />
        <Button
          onClick={openAdd}
          className="bg-gray-900 hover:bg-gray-800 text-white"
          data-testid="add-firm-btn"
        >
          <Plus size={16} className="mr-1.5" />
          Add firm
        </Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <Input
            placeholder="Search firms…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="admin-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]" data-testid="admin-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="Live">Live</SelectItem>
            <SelectItem value="Renewal Pending">Renewal Pending</SelectItem>
            <SelectItem value="Churned">Churned</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tierFilter} onValueChange={setTierFilter}>
          <SelectTrigger className="w-[140px]" data-testid="admin-tier-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            {tierOptions.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="admin-firm-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left">
                <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                  Firm
                </th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                  Users
                </th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                  Tier
                </th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                  POC
                </th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                  Activation
                </th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                  Status
                </th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && firms.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-400">
                    No firms found.
                  </td>
                </tr>
              )}
              {!loading &&
                firms.map((f) => (
                  <tr
                    key={f.subdomain}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    data-testid={`admin-row-${f.subdomain}`}
                  >
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/firms/${f.subdomain}${firmFromQS(location)}`)}
                        className="font-medium text-gray-900 hover:text-violet-700 hover:underline text-left"
                        data-testid={`admin-firm-link-${f.subdomain}`}
                      >
                        {f.firm_name}
                      </button>
                      <FirmSubmeta firm={f} />
                    </td>
                    <td className="px-4 py-3">
                      <UsersCell value={f.num_users} />
                    </td>
                    <td className="px-4 py-3">
                      <TierBadge tier={f.tier} />
                    </td>
                    <td className="px-4 py-3"><PocChipPair poc={f.poc} salesPoc={f.sales_poc} /></td>
                    <td className="px-4 py-3 text-gray-700 font-mono text-xs">{f.activation_date}</td>
                    <td className="px-4 py-3">
                      {f.status === "Live" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          Live
                        </span>
                      ) : f.status === "Renewal Pending" ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs font-medium text-amber-700"
                          data-testid={`status-renewal-pending-${f.subdomain}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          Renewal Pending
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                          Churned
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleStatus(f)}
                          data-testid={`toggle-status-${f.subdomain}`}
                        >
                          {f.status === "Live" || f.status === "Renewal Pending" ? (
                            <XCircle size={14} className="text-gray-500" />
                          ) : (
                            <CheckCircle size={14} className="text-emerald-600" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEdit(f)}
                          data-testid={`edit-${f.subdomain}`}
                        >
                          <PencilSimple size={14} />
                        </Button>
                        {isSuperAdmin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => remove(f)}
                            data-testid={`delete-${f.subdomain}`}
                          >
                            <Trash size={14} className="text-red-500" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {pages > 1 ? (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs">
            <div className="text-gray-500" data-testid="admin-pagination-info">
              Page {page} of {pages} · {total.toLocaleString("en-IN")} firms
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="admin-prev-page"
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= pages}
                onClick={() => setPage((p) => p + 1)}
                data-testid="admin-next-page"
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <SeedNumUsersDialog open={showSeedDlg} onOpenChange={setShowSeedDlg} />
      <ImportCloudPlanDialog open={showCloudDlg} onOpenChange={setShowCloudDlg} />
    </div>
  );
}

function SeedNumUsersDialog({ open, onOpenChange }) {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async (dryRun) => {
    if (!file) {
      setError("Please choose an .xlsx file first.");
      return;
    }
    setError("");
    setSubmitting(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(
        `/firms/seed-num-users?dry_run=${dryRun ? "true" : "false"}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setResult({ ...r.data, dry_run: dryRun });
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="seed-num-users-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <UploadSimple size={18} className="text-violet-600" />
            Import user counts (num_users)
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2 text-sm">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900 leading-relaxed">
            Upload the <span className="font-mono">No_of_users_and_URL_cleaned.xlsx</span> file (or save as CSV and upload that).
            Multi-branch firms (same URL, multiple rows) are summed automatically.
            Manual overrides (PDW / VNC / MSS / TRR / ECCA / Guru &amp; Jana) take precedence over the file.
            <span className="block mt-1.5 font-medium">
              Firms not in the file keep their existing value — values entered manually via this form are never overwritten with "—".
            </span>
            <span className="block mt-1.5">
              Use <span className="font-mono">Preview changes</span> first to see what will be written before committing.
            </span>
          </div>
          <div>
            <input
              type="file"
              accept=".xlsx,.csv"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setError(""); }}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
              data-testid="seed-num-users-file"
            />
            {file && <div className="text-[11px] text-gray-500 mt-1">Selected: {file.name} ({Math.round(file.size / 1024)} KB)</div>}
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-2.5 text-xs">{error}</div>
          )}
          {result && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <div className="text-xs font-semibold mb-2 flex items-center gap-2">
                {result.dry_run ? (
                  <span className="text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider">DRY RUN — no changes saved</span>
                ) : (
                  <span className="text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider">Applied</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-700">
                <div>Total firms in DB:</div><div className="font-mono tabular-nums text-right">{result.summary.total_firms}</div>
                <div>Excel URLs read:</div><div className="font-mono tabular-nums text-right">{result.summary.excel_urls}</div>
                <div>From excel:</div><div className="font-mono tabular-nums text-right">{result.summary.set_from_excel}</div>
                <div>From manual override:</div><div className="font-mono tabular-nums text-right">{result.summary.set_from_manual_override}</div>
                <div>Set to "—" (no match):</div><div className="font-mono tabular-nums text-right">{result.summary.set_to_dash_default}</div>
                <div>Preserved manual edits:</div><div className="font-mono tabular-nums text-right">{result.summary.preserved_manual_edit}</div>
                <div className="text-gray-500">Already correct:</div><div className="font-mono tabular-nums text-right text-gray-500">{result.summary.unchanged}</div>
              </div>
              {result.sample_changes?.length > 0 && (
                <div className="mt-3 border-t pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-medium mb-1.5">
                    Sample changes (first {result.sample_changes.length})
                  </div>
                  <div className="max-h-[180px] overflow-y-auto text-[11px] font-mono">
                    {result.sample_changes.map((c, i) => (
                      <div key={`samp-${c.subdomain || i}-${c.from}-${c.to}`} className="flex items-center justify-between gap-2 py-0.5 border-b border-gray-100 last:border-0">
                        <span className="truncate">{c.firm_name}</span>
                        <span className="text-gray-500 shrink-0">{c.from} → <span className="text-emerald-700 font-semibold">{c.to}</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 mt-4 flex-wrap">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button
            variant="outline"
            disabled={!file || submitting}
            onClick={() => run(true)}
            data-testid="seed-num-users-preview"
          >
            {submitting ? "Working…" : "Preview changes"}
          </Button>
          <Button
            disabled={!file || submitting}
            onClick={() => run(false)}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="seed-num-users-apply"
          >
            {submitting ? "Working…" : "Apply changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportCloudPlanDialog({ open, onOpenChange }) {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    if (!file) {
      setError("Please choose an .xlsx file first.");
      return;
    }
    setError("");
    setSubmitting(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(
        "/admin/firms/cloud-plan/import",
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="import-cloud-plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <UploadSimple size={18} className="text-violet-600" />
            Import cloud plan (Enterprise only)
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2 text-sm">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900 leading-relaxed">
            Upload an .xlsx with these columns (header row, exact names — case-insensitive):
            <div className="mt-1.5 font-mono text-[11px]">
              firm_url · Own storage · AssureAI cloud
            </div>
            Each row must have <span className="font-mono">Yes</span> in exactly one of <span className="font-mono">Own storage</span> or <span className="font-mono">AssureAI cloud</span>.
            Rows with both/neither, unknown firms, or non-Enterprise firms are skipped and listed below.
          </div>
          <div>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setError(""); }}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
              data-testid="import-cloud-plan-file"
            />
            {file && <div className="text-[11px] text-gray-500 mt-1">Selected: {file.name} ({Math.round(file.size / 1024)} KB)</div>}
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-2.5 text-xs">{error}</div>
          )}
          {result && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-700">
                <div>Total rows in file:</div><div className="font-mono tabular-nums text-right">{result.summary.total_rows}</div>
                <div>Updated:</div><div className="font-mono tabular-nums text-right text-emerald-700 font-semibold">{result.summary.updated}</div>
                <div>Skipped:</div><div className="font-mono tabular-nums text-right text-amber-700">{result.summary.skipped}</div>
              </div>
              {result.skipped?.length > 0 && (
                <div className="mt-3 border-t pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-medium mb-1.5">
                    Skipped rows
                  </div>
                  <div className="max-h-[180px] overflow-y-auto text-[11px] font-mono">
                    {result.skipped.map((r, i) => (
                      <div key={`skip-${i}-${r.firm_url}`} className="flex items-center justify-between gap-2 py-0.5 border-b border-gray-100 last:border-0">
                        <span className="truncate">{r.firm_url}</span>
                        <span className="text-gray-500 shrink-0">{r.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 mt-4 flex-wrap">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button
            disabled={!file || submitting}
            onClick={run}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="import-cloud-plan-apply"
          >
            {submitting ? "Working…" : "Apply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({ label, children, required = false, hint }) {
  return (
    <div>
      <Label className="text-xs font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-[10px] text-gray-500 mt-1 leading-snug">{hint}</div>}
    </div>
  );
}


// ---------------- Bulk Import Dialog ----------------
const TEMPLATE_LINK = "/api/firms/import/new-customer-template";

function BulkImportDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [headerError, setHeaderError] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setHeaderError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/firms/import/new-customer-template", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "new_customers_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download template");
    }
  };

  const submit = async () => {
    if (!file) return;
    setSubmitting(true);
    setResult(null);
    setHeaderError(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/firms/import/new-customers", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      if (r.data.summary.errors === 0) {
        toast.success(
          `Imported · ${r.data.summary.created} new, ${r.data.summary.updated} updated, ${r.data.summary.unchanged} unchanged`,
        );
      } else {
        toast.warning(`Imported with ${r.data.summary.errors} error row(s)`);
      }
      // Don't auto-close — let user review the report
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.expected) {
        setHeaderError(d);
      } else {
        toast.error(typeof d === "string" ? d : "Import failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          // If we have results, refresh the parent table on close
          if (result && (result.summary.created || result.summary.updated)) onImported();
          reset();
        }
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-2xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <UploadSimple size={18} />
            Bulk import new customers
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          {/* Template */}
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-1">
              Required CSV template — exactly these 6 columns
            </div>
            <div className="font-mono text-[11px] text-gray-700 bg-white border border-gray-200 rounded px-2 py-1.5 mb-2 overflow-x-auto whitespace-nowrap">
              url, firm_name, tier, city, poc, activation_date
            </div>
            <div className="text-[11px] text-gray-600 leading-relaxed space-y-1">
              <div><span className="font-mono">url</span> — unique key. e.g. <span className="font-mono">ksp.assureai.in</span> or <span className="font-mono">ksp</span>. Existing rows are updated; new rows are inserted.</div>
              <div><span className="font-mono">tier</span> — Premium / Enterprise / Standard</div>
              <div><span className="font-mono">poc</span> — Shankar / Onkar / Vaibhav / Sasikiran / Rajaneesh</div>
              <div><span className="font-mono">activation_date</span> — YYYY-MM-DD, not in the future</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadTemplate}
              className="mt-3"
              data-testid="download-template-btn"
            >
              Download template
            </Button>
          </div>

          {/* File picker */}
          <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center">
            <input
              ref={inputRef}
              type="file"
              accept=".csv"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                setResult(null);
                setHeaderError(null);
              }}
              className="block mx-auto text-xs"
              data-testid="bulk-import-file"
            />
            {file && (
              <div className="mt-2 text-[11px] text-gray-600 font-mono">
                Selected: {file.name} · {(file.size / 1024).toFixed(1)} KB
              </div>
            )}
          </div>

          {/* Header error */}
          {headerError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs text-red-800 space-y-1" data-testid="header-error">
              <div className="font-semibold">{headerError.message}</div>
              {headerError.missing?.length > 0 && (
                <div>Missing columns: <span className="font-mono">{headerError.missing.join(", ")}</span></div>
              )}
              {headerError.extra?.length > 0 && (
                <div>Unexpected columns: <span className="font-mono">{headerError.extra.join(", ")}</span></div>
              )}
              <div className="text-red-600">Hint: download the template below and use it as-is.</div>
            </div>
          )}

          {/* Result report */}
          {result && (
            <div className="border border-gray-200 rounded-md overflow-hidden" data-testid="import-report">
              <div className="grid grid-cols-4 gap-px bg-gray-100 text-center text-xs">
                <Stat n={result.summary.created} label="Created" color="text-emerald-700" bg="bg-emerald-50" />
                <Stat n={result.summary.updated} label="Updated" color="text-blue-700" bg="bg-blue-50" />
                <Stat n={result.summary.unchanged} label="Unchanged" color="text-gray-700" bg="bg-gray-50" />
                <Stat n={result.summary.errors} label="Errors" color="text-red-700" bg="bg-red-50" />
              </div>
              {result.errors.length > 0 && (
                <div className="border-t border-gray-200 bg-red-50/40">
                  <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-red-700 font-medium">Errors</div>
                  <div className="max-h-[140px] overflow-y-auto px-3 pb-3 space-y-1 text-xs">
                    {result.errors.map((e, i) => (
                      <div key={`err-${e.row ?? i}-${e.url || i}`} className="font-mono text-red-700">
                        Row {e.row} · {e.url || "(no url)"} — {e.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {result.updated.length > 0 && (
                <div className="border-t border-gray-200">
                  <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-blue-700 font-medium bg-blue-50/40">Field changes</div>
                  <div className="max-h-[160px] overflow-y-auto px-3 pb-3 space-y-2 text-xs">
                    {result.updated.map((u) => (
                      <div key={u.subdomain} className="bg-white border border-gray-200 rounded px-2 py-1.5">
                        <div className="font-mono text-gray-700 text-[11px]">{u.url}</div>
                        <div className="text-[11px] text-gray-500 space-y-0.5 mt-0.5">
                          {Object.entries(u.changes).map(([field, { old, new: nv }]) => (
                            <div key={field}>
                              <span className="font-mono text-gray-700">{field}:</span>{" "}
                              <span className="line-through text-red-500">{String(old)}</span>{" "}→{" "}
                              <span className="text-emerald-700">{String(nv)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {result ? "Done" : "Cancel"}
          </Button>
          <Button
            onClick={submit}
            disabled={!file || submitting}
            className="bg-gray-900 hover:bg-gray-800 text-white"
            data-testid="bulk-import-submit"
          >
            {submitting ? "Uploading…" : result ? "Re-upload" : "Upload & import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ n, label, color, bg }) {
  return (
    <div className={`${bg} py-3`}>
      <div className={`font-mono font-semibold text-xl ${color}`}>{n}</div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

// ---------------- POC Manager Dialog (super admin only) ----------------
function PocManagerDialog({ open, onClose, onChanged }) {
  const confirm = useConfirm();
  const [pocs, setPocs] = useState([]);
  const [pocsDetail, setPocsDetail] = useState({}); // name -> { live_firms }
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // {name, draft}
  const [savingRename, setSavingRename] = useState(false);
  const [deletingName, setDeletingName] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/pocs");
      setPocs(r.data.pocs || []);
      const detail = {};
      for (const row of r.data.pocs_detail || []) detail[row.name] = row;
      setPocsDetail(detail);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load POCs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) { load(); setNewName(""); setEditing(null); } }, [open]);

  const removePoc = async (name) => {
    if (!await confirm({
      title: `Delete POC "${name}"?`,
      body: "This cannot be undone.",
      confirmLabel: "Delete POC",
      destructive: true,
    })) return;
    setDeletingName(name);
    try {
      await api.delete(`/admin/pocs/${encodeURIComponent(name)}`);
      toast.success(`POC "${name}" deleted`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingName(null);
    }
  };

  const addNew = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await api.post("/admin/pocs", { name });
      toast.success(`POC "${name}" added`);
      setNewName("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add POC");
    } finally {
      setAdding(false);
    }
  };

  const saveRename = async () => {
    if (!editing) return;
    const draft = editing.draft.trim();
    if (!draft || draft === editing.name) { setEditing(null); return; }
    setSavingRename(true);
    try {
      const r = await api.patch(`/admin/pocs/${encodeURIComponent(editing.name)}`, { new_name: draft });
      const fu = r.data.firms_updated || 0;
      const uu = r.data.users_updated || 0;
      toast.success(
        `Renamed to "${r.data.name}"` +
        (fu || uu ? ` \u00b7 updated ${fu} firm(s)${uu ? `, ${uu} user(s)` : ""}` : "")
      );
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    } finally {
      setSavingRename(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="poc-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Users size={18} className="text-gray-700" />
            Manage POCs
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          <div>
            <Label className="text-xs font-medium text-gray-700">Add new POC</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addNew()}
                placeholder="Full name (e.g. Priya)"
                disabled={adding}
                data-testid="poc-new-name"
              />
              <Button
                onClick={addNew}
                disabled={!newName.trim() || adding}
                className="bg-gray-900 hover:bg-gray-800 text-white"
                data-testid="poc-add-btn"
              >
                {adding ? "Adding\u2026" : "Add"}
              </Button>
            </div>
            <div className="text-[10px] text-gray-500 mt-1">
              POCs added here appear instantly in every dropdown and firm-assignment list across the app.
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium text-gray-700">Existing POCs</Label>
            <div className="mt-1.5 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-[260px] overflow-y-auto">
              {loading && <div className="px-3 py-4 text-sm text-gray-400 text-center">Loading\u2026</div>}
              {!loading && pocs.length === 0 && (
                <div className="px-3 py-4 text-sm text-gray-400 text-center">No POCs yet.</div>
              )}
              {!loading && pocs.map((p) => {
                const isEditing = editing?.name === p;
                return (
                  <div key={p} className="px-3 py-2 flex items-center gap-2" data-testid={`poc-row-${p}`}>
                    {isEditing ? (
                      <>
                        <Input
                          value={editing.draft}
                          onChange={(e) => setEditing({ ...editing, draft: e.target.value })}
                          onKeyDown={(e) => e.key === "Enter" && saveRename()}
                          autoFocus
                          className="h-8"
                          data-testid={`poc-rename-input-${p}`}
                        />
                        <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="h-8 px-2">
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={saveRename}
                          disabled={savingRename || !editing.draft.trim()}
                          className="h-8 px-2 bg-gray-900 text-white hover:bg-gray-800"
                          data-testid={`poc-rename-save-${p}`}
                        >
                          {savingRename ? "Saving\u2026" : "Save"}
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 text-sm text-gray-800">
                          {p}
                          {pocsDetail[p]?.live_firms > 0 && (
                            <span className="ml-2 text-[10px] text-gray-400 font-mono">
                              · {pocsDetail[p].live_firms} live
                            </span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing({ name: p, draft: p })}
                          className="h-8 px-2"
                          data-testid={`poc-rename-btn-${p}`}
                        >
                          <PencilSimple size={13} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removePoc(p)}
                          disabled={deletingName === p || (pocsDetail[p]?.live_firms || 0) > 0}
                          title={
                            (pocsDetail[p]?.live_firms || 0) > 0
                              ? `Cannot delete — ${pocsDetail[p].live_firms} live firm(s) still assigned`
                              : "Delete POC"
                          }
                          className="h-8 px-2 text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent"
                          data-testid={`poc-delete-btn-${p}`}
                        >
                          <Trash size={13} />
                        </Button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---- Aliases editor (super_admin only, inside Edit Firm dialog) ----------
// `aliases` is the list of extra firm URLs whose Mixpanel/Grafana events the
// ETL folds into this firm. Removing a URL here stops the aggregation — the
// next ETL run will treat those events as their own firm's data.
function AliasesEditor({ firm, onChange }) {
  const [aliases, setAliases] = useState(firm.aliases || []);
  const [newAlias, setNewAlias] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setAliases(firm.aliases || []); }, [firm.aliases]);

  const persist = async (next) => {
    setBusy(true);
    try {
      const r = await api.patch(`/firms/${firm.subdomain}/aliases`, { aliases: next });
      const saved = r.data.aliases || [];
      setAliases(saved);
      onChange?.(saved);
      toast.success("Aliases updated — next ETL run will refresh events.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update aliases");
    } finally {
      setBusy(false);
    }
  };

  const remove = (url) => persist(aliases.filter((a) => a !== url));
  const add = () => {
    const v = newAlias.trim().toLowerCase();
    if (!v) return;
    if (aliases.includes(v)) {
      toast.error("Alias already present");
      return;
    }
    persist([...aliases, v]);
    setNewAlias("");
  };

  return (
    <div data-testid="aliases-editor">
      <div className="flex items-baseline justify-between mb-2">
        <Label className="text-xs font-medium text-gray-700">
          Aliases (ETL aggregation)
        </Label>
        <span className="text-[10px] text-gray-400">super_admin only</span>
      </div>
      <div className="text-[11px] text-gray-500 mb-2 leading-snug">
        Every URL listed here has its Mixpanel + Grafana events folded into this
        firm. Remove a URL to stop the aggregation; events for that URL will
        flow to whichever firm now claims it as its primary <span className="font-mono">firm_url</span>
        (or be dropped if no firm owns it). Takes effect on the next ETL run.
      </div>
      {aliases.length === 0 ? (
        <div className="text-xs text-gray-400 italic mb-2">No aliases — this firm only collects events for its own firm_url.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="aliases-list">
          {aliases.map((a) => (
            <span
              key={a}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-[11px] text-violet-700 font-mono"
            >
              {a}
              <button
                type="button"
                onClick={() => remove(a)}
                disabled={busy}
                className="text-violet-600 hover:text-red-600 disabled:opacity-40"
                title={`Remove ${a}`}
                data-testid={`alias-remove-${a}`}
              >
                <X size={10} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input
          placeholder="e.g. assureai-tax.gurujana.com"
          value={newAlias}
          onChange={(e) => setNewAlias(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          disabled={busy}
          className="h-8 text-xs font-mono"
          data-testid="alias-input"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          disabled={busy || !newAlias.trim()}
          data-testid="alias-add-btn"
        >
          Add
        </Button>
      </div>
    </div>
  );
}
